import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  BookOpenIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const API_URL = (
  process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1'
).replace(/\/+$/, '');

const API_BASE_URL = API_URL.replace(/\/api\/v1$/, '');

const BOOKS_PER_PAGE = 12;
const SKELETON_COUNT = 8;
const LOAD_MORE_SKELETON_COUNT = 4;
const HERO_LINE_ONE = 'Find a book. Start learning.';
const HERO_LINE_TWO = 'Grow every day.';
const HERO_TEXT = `${HERO_LINE_ONE}\n${HERO_LINE_TWO}`;

const createFeedSeed = () => {
  const webCrypto = typeof window !== 'undefined' ? window.crypto : null;

  if (webCrypto?.getRandomValues) {
    const values = new Uint32Array(4);
    webCrypto.getRandomValues(values);
    return Array.from(values, (value) => value.toString(36)).join('-');
  }

  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 12)}`;
};

const normaliseBook = (book) => ({
  ...book,
  cover:
    book.cover ||
    (book.cover_image
      ? `${API_BASE_URL}/covers/${book.cover_image.split('/').pop()}`
      : null),
});

const formatPrice = (book) => {
  if (!book.premium) return 'Free';

  const amount = Number.parseFloat(book.price);
  const currency = String(book.currency || 'SLE').toUpperCase();
  return `${currency} ${Number.isFinite(amount) ? amount.toFixed(2) : '0.00'}`;
};

function TypingHero() {
  const [characterCount, setCharacterCount] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (reduceMotion) {
      setCharacterCount(HERO_TEXT.length);
      return undefined;
    }

    const completed = characterCount >= HERO_TEXT.length;
    let delay = 52;

    if (completed && !isPaused) {
      delay = 2200;
    } else if (completed && isPaused) {
      delay = 450;
    }

    const timer = window.setTimeout(() => {
      if (completed && !isPaused) {
        setIsPaused(true);
        return;
      }

      if (completed && isPaused) {
        setCharacterCount(0);
        setIsPaused(false);
        return;
      }

      setCharacterCount((current) => current + 1);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [characterCount, isPaused]);

  const typedLineOne = HERO_TEXT.slice(
    0,
    Math.min(characterCount, HERO_LINE_ONE.length)
  );
  const secondLineStart = HERO_LINE_ONE.length + 1;
  const typedLineTwo =
    characterCount > secondLineStart
      ? HERO_TEXT.slice(secondLineStart, characterCount)
      : '';

  return (
    <h1
      className="landing-soft-enter relative mx-auto max-w-3xl text-4xl font-extrabold leading-tight tracking-tight text-gray-950 sm:text-5xl lg:text-6xl"
      aria-label={`${HERO_LINE_ONE} ${HERO_LINE_TWO}`}
    >
      <span className="invisible" aria-hidden="true">
        {HERO_LINE_ONE}
        <span className="block">{HERO_LINE_TWO}</span>
      </span>

      <span className="absolute inset-0" aria-hidden="true">
        {typedLineOne}
        {characterCount <= HERO_LINE_ONE.length && (
          <span className="landing-typing-cursor" />
        )}
        <span className="block text-primary-600">
          {typedLineTwo}
          {characterCount > HERO_LINE_ONE.length && (
            <span className="landing-typing-cursor" />
          )}
        </span>
      </span>
    </h1>
  );
}

function BookSkeleton({ index }) {
  return (
    <div
      className="overflow-hidden rounded-xl border border-gray-100 bg-white"
      style={{ animationDelay: `${index * 35}ms` }}
      aria-hidden="true"
    >
      <div className="landing-shimmer h-52 bg-gray-100 sm:h-64" />
      <div className="space-y-2.5 p-4">
        <div className="landing-shimmer h-4 w-4/5 rounded bg-gray-100" />
        <div className="landing-shimmer h-3.5 w-3/5 rounded bg-gray-100" />
        <div className="flex items-center justify-between pt-2">
          <div className="landing-shimmer h-5 w-16 rounded bg-gray-100" />
          <div className="landing-shimmer h-5 w-12 rounded bg-gray-100" />
        </div>
      </div>
    </div>
  );
}

function BookCover({ book, eager }) {
  const [status, setStatus] = useState(book.cover ? 'loading' : 'empty');

  if (!book.cover || status === 'error') {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-50">
        <BookOpenIcon className="h-16 w-16 text-gray-300" />
      </div>
    );
  }

  return (
    <>
      {status !== 'loaded' && (
        <div className="landing-shimmer absolute inset-0 bg-gray-100" />
      )}
      <img
        src={book.cover}
        alt={book.title}
        className={`h-full w-full bg-white object-contain p-2 transition-opacity duration-300 ease-out ${
          status === 'loaded' ? 'opacity-100' : 'opacity-0'
        }`}
        loading={eager ? 'eager' : 'lazy'}
        fetchPriority={eager ? 'high' : 'auto'}
        decoding="async"
        onLoad={() => setStatus('loaded')}
        onError={() => setStatus('error')}
      />
    </>
  );
}

function BookCard({ book, index, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(book)}
      className="landing-book-enter group overflow-hidden rounded-xl border border-gray-100 bg-white text-left transition-[border-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      style={{ animationDelay: `${(index % BOOKS_PER_PAGE) * 25}ms` }}
    >
      <div className="relative h-52 overflow-hidden bg-white sm:h-64">
        <BookCover book={book} eager={index < 4} />
        <span className="absolute left-2 top-2 rounded-md border border-gray-200 bg-white/95 px-2 py-1 text-[11px] font-semibold text-gray-700">
          {book.premium ? formatPrice(book) : 'Free'}
        </span>
      </div>

      <div className="p-4">
        <h3 className="line-clamp-2 min-h-[2.5rem] text-sm font-bold leading-5 text-gray-900 sm:text-base">
          {book.title}
        </h3>
        <p className="mt-1 truncate text-xs text-gray-500 sm:text-sm">
          {book.author || 'Unknown author'}
        </p>
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-gray-100 pt-3">
          <span className="truncate text-xs font-medium text-primary-700">
            {book.sector || 'General'}
          </span>
          <span className="flex flex-none items-center gap-1 text-xs font-semibold text-gray-500 transition-colors group-hover:text-primary-600">
            View
            <ArrowRightIcon className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </button>
  );
}

export default function LandingPage() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [loadMoreError, setLoadMoreError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sector, setSector] = useState('All');
  const [selectedBook, setSelectedBook] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    hasNextPage: false,
    nextPage: null,
  });
  const requestRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);
  // A new component view creates a new mixed feed. This same seed is sent
  // with every later page so scrolling never duplicates or skips books.
  const feedSeedRef = useRef(createFeedSeed());

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const nextSearch = searchInput.trim();

      if (nextSearch) setSector('All');
      setSearch(nextSearch);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const fetchBooks = useCallback(
    async ({ page = 1, append = false } = {}) => {
      if (append && requestRef.current) return;
      if (!append && requestRef.current) requestRef.current.abort();

      const controller = new AbortController();
      requestRef.current = controller;

      if (append) {
        setLoadingMore(true);
        setLoadMoreError('');
      } else {
        setLoading(true);
        setError('');
      }

      try {
        // Use a standalone public request instead of the authenticated
        // apiClient. This prevents a global 401 interceptor from sending a
        // web visitor to Login when the public endpoint is unavailable.
        const query = new URLSearchParams({
          page: String(page),
          limit: String(BOOKS_PER_PAGE),
          feedSeed: feedSeedRef.current,
        });

        if (search) {
          // Search the entire public catalogue even when a filter was selected
          // earlier. This prevents a hidden sector filter from blocking a
          // matching title, author, subject or syllabus.
          query.set('search', search);
        } else if (sector === 'Syllabus') {
          // Syllabus is a catalogue type, not always the stored sector.
          // For example, "Social Studies Syllabus" may have sector
          // "Social Studies". Search across title, sector and description.
          query.set('search', 'syllabus');
        } else if (sector !== 'All') {
          query.set('sector', sector);
        }

        const response = await fetch(
          `${API_URL}/books/public?${query.toString()}`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            signal: controller.signal,
          }
        );

        let payload = null;

        try {
          payload = await response.json();
        } catch (_) {
          // A proxy or unavailable server may return a non-JSON error page.
        }

        if (!response.ok || !payload?.success) {
          throw new Error(
            payload?.message ||
              `Unable to load books (${response.status}).`
          );
        }

        const incoming = (payload.data || []).map(normaliseBook);

        setBooks((current) => {
          if (!append) return incoming;

          const existing = new Set(current.map((book) => String(book.id)));
          return [
            ...current,
            ...incoming.filter((book) => !existing.has(String(book.id))),
          ];
        });

        const nextPagination = payload.pagination || {};
        if (nextPagination.feedSeed) {
          feedSeedRef.current = nextPagination.feedSeed;
        }
        setPagination({
          page: Number(nextPagination.page) || page,
          hasNextPage: Boolean(nextPagination.hasNextPage),
          nextPage: nextPagination.nextPage || null,
        });
      } catch (requestError) {
        if (
          requestError.name !== 'CanceledError' &&
          requestError.name !== 'AbortError'
        ) {
          const message =
            requestError.message ||
            'Unable to load books. Please try again.';

          if (append) {
            setLoadMoreError(message);
          } else {
            setError(message);
            setBooks([]);
          }
        }
      } finally {
        if (requestRef.current === controller) {
          requestRef.current = null;
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [search, sector]
  );

  useEffect(() => {
    fetchBooks({ page: 1, append: false });

    return () => {
      requestRef.current?.abort();
    };
  }, [fetchBooks]);

  const loadNextPage = useCallback(() => {
    if (
      loading ||
      loadingMore ||
      requestRef.current ||
      loadMoreError ||
      !pagination.hasNextPage
    ) {
      return;
    }

    fetchBooks({
      page: pagination.nextPage || pagination.page + 1,
      append: true,
    });
  }, [
    fetchBooks,
    loading,
    loadingMore,
    loadMoreError,
    pagination,
  ]);

  useEffect(() => {
    const trigger = loadMoreTriggerRef.current;

    if (
      !trigger ||
      !pagination.hasNextPage ||
      loadMoreError ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadNextPage();
      },
      {
        root: null,
        rootMargin: '650px 0px',
        threshold: 0.01,
      }
    );

    observer.observe(trigger);
    return () => observer.disconnect();
  }, [loadNextPage, loadMoreError, pagination.hasNextPage]);

  const openBookModal = useCallback((book) => {
    setSelectedBook(book);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setModalVisible(true));
    });
  }, []);

  const closeBookModal = useCallback(() => {
    setModalVisible(false);
    window.setTimeout(() => setSelectedBook(null), 220);
  }, []);

  const selectCategory = useCallback((item) => {
    setSearchInput('');
    setSearch('');
    setSector(item);
  }, []);

  useEffect(() => {
    if (!selectedBook) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') closeBookModal();
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [selectedBook, closeBookModal]);

  const sectors = useMemo(() => {
    const values = books
      .map((book) => String(book.sector || '').trim())
      .filter(Boolean);

    const unique = Array.from(new Set(values)).filter(
      (value) => value.toLowerCase() !== 'syllabus'
    );

    return ['All', 'Syllabus', ...unique.sort()];
  }, [books]);

  const loginRedirect = selectedBook
    ? `/login?redirect=${encodeURIComponent(`/?book=${selectedBook.id}`)}`
    : '/login';
  const registerRedirect = selectedBook
    ? `/register?redirect=${encodeURIComponent(`/?book=${selectedBook.id}`)}`
    : '/register';

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      <style>{`
        html { scroll-behavior: smooth; }
        @keyframes landingShimmer {
          from { background-position: 200% 0; }
          to { background-position: -200% 0; }
        }
        @keyframes landingBookEnter {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes landingSoftEnter {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes landingCursorBlink {
          0%, 45% { opacity: 1; }
          46%, 100% { opacity: 0; }
        }
        .landing-shimmer {
          background: linear-gradient(90deg, #f3f4f6 25%, #ffffff 50%, #f3f4f6 75%);
          background-size: 200% 100%;
          animation: landingShimmer 1.35s ease-in-out infinite;
        }
        .landing-book-enter {
          animation: landingBookEnter .42s ease-out backwards;
        }
        .landing-soft-enter {
          animation: landingSoftEnter .65s cubic-bezier(.2,.8,.2,1) backwards;
        }
        .landing-delay-1 { animation-delay: 90ms; }
        .landing-delay-2 { animation-delay: 180ms; }
        .landing-typing-cursor {
          display: inline-block;
          width: .075em;
          height: .88em;
          margin-left: .1em;
          border-radius: 999px;
          background: currentColor;
          vertical-align: -.04em;
          animation: landingCursorBlink .8s steps(1, end) infinite;
        }
        .landing-filter-scroll {
          scrollbar-width: none;
          scroll-behavior: smooth;
        }
        .landing-filter-scroll::-webkit-scrollbar { display: none; }
        .landing-modal-backdrop {
          opacity: 0;
          transition: opacity 220ms ease;
        }
        .landing-modal-backdrop.visible { opacity: 1; }
        .landing-modal-panel {
          opacity: 0;
          transform: translateY(18px) scale(.985);
          transition: opacity 220ms ease, transform 220ms cubic-bezier(.2,.8,.2,1);
        }
        .landing-modal-panel.visible {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
        @media (prefers-reduced-motion: reduce) {
          html { scroll-behavior: auto; }
          .landing-shimmer, .landing-book-enter, .landing-soft-enter,
          .landing-typing-cursor { animation: none; }
          .landing-modal-backdrop, .landing-modal-panel { transition: none; }
        }
      `}</style>

      <header className="sticky top-0 z-40 border-b border-gray-100 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2.5">
            <img
              src="/assets/app-logo.png"
              alt="PawaLibrary"
              className="h-9 w-9 object-contain"
            />
            <span className="text-lg font-bold tracking-tight sm:text-xl">
              PawaLibrary
            </span>
          </Link>

          <nav aria-label="Main navigation">
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary-700 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              Get started
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="border-b border-gray-100 bg-[#fafbfa]">
          <div className="mx-auto max-w-7xl px-4 py-14 text-center sm:px-6 sm:py-20 lg:px-8">
            <TypingHero />
            <p className="landing-soft-enter landing-delay-1 mx-auto mt-5 max-w-2xl text-lg leading-8 text-gray-600">
              Explore educational books for students, teachers and lifelong learners.
            </p>
            <div className="landing-soft-enter landing-delay-2 mx-auto mt-8 max-w-2xl">
              <label htmlFor="public-book-search" className="sr-only">
                Search books
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  id="public-book-search"
                  type="search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search by title, author or subject"
                  className="w-full rounded-xl border border-gray-200 bg-white py-3.5 pl-11 pr-4 text-base outline-none transition focus:border-primary-400 focus:ring-4 focus:ring-primary-100"
                />
              </div>
            </div>
          </div>
        </section>

        <section id="books" className="py-12 sm:py-16">
          <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
            <div className="landing-soft-enter flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-primary-600">Browse the library</p>
                <h2 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">
                  {search ? `Results for “${search}”` : 'Available books'}
                </h2>
              </div>

              {sectors.length > 1 && (
                <div className="landing-filter-scroll flex max-w-full gap-2 overflow-x-auto pb-1">
                  {sectors.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => selectCategory(item)}
                      className={`flex-none rounded-lg border px-3.5 py-2 text-sm font-semibold transition-all duration-200 ${
                        sector === item
                          ? 'border-primary-600 bg-primary-600 text-white'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-primary-200 hover:text-primary-700'
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="landing-soft-enter landing-delay-1 mt-7 min-w-0">
            {loading ? (
              <div
                className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4"
                aria-label="Loading books"
                aria-busy="true"
              >
                {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                  <BookSkeleton key={index} index={index} />
                ))}
              </div>
            ) : error ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-5 py-10 text-center">
                <BookOpenIcon className="mx-auto h-12 w-12 text-gray-300" />
                <p className="mt-3 font-semibold text-gray-700">We could not load the books.</p>
                <p className="mt-1 text-sm text-gray-500">{error}</p>
                <button
                  type="button"
                  onClick={() => fetchBooks({ page: 1 })}
                  className="mt-4 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
                >
                  Try again
                </button>
              </div>
            ) : books.length === 0 ? (
              <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white px-5 py-12 text-center sm:px-8 sm:py-16">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50 text-primary-600">
                  <MagnifyingGlassIcon className="h-8 w-8" />
                </div>

                <h3 className="mt-5 text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
                  {search
                    ? `No match for “${search}”`
                    : sector !== 'All'
                      ? `No books found in ${sector}`
                      : 'No books are available yet'}
                </h3>

                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-gray-500 sm:text-base">
                  {search
                    ? 'We searched titles, authors, subjects and descriptions. Check the spelling or try a shorter phrase.'
                    : 'There are no available books in this category right now. You can return to the full library.'}
                </p>

                {search && (
                  <div className="mt-5 flex flex-wrap justify-center gap-2 text-xs font-medium text-gray-500">
                    {['Try fewer words', 'Search by author', 'Search by subject'].map(
                      (tip) => (
                        <span
                          key={tip}
                          className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5"
                        >
                          {tip}
                        </span>
                      )
                    )}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => {
                    setSearchInput('');
                    setSearch('');
                    setSector('All');
                  }}
                  className="mt-6 inline-flex items-center justify-center rounded-xl bg-primary-600 px-5 py-3 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                >
                  View all books
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
                  {books.map((book, index) => (
                    <BookCard
                      key={book.id}
                      book={book}
                      index={index}
                      onSelect={openBookModal}
                    />
                  ))}
                </div>

                {pagination.hasNextPage && !loadMoreError && (
                  <div
                    ref={loadMoreTriggerRef}
                    className="mt-6 min-h-6"
                    aria-label={loadingMore ? 'Loading more books' : undefined}
                    aria-busy={loadingMore}
                  >
                    {loadingMore && (
                      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
                        {Array.from({ length: LOAD_MORE_SKELETON_COUNT }).map(
                          (_, index) => (
                            <BookSkeleton
                              key={`more-book-skeleton-${index}`}
                              index={index}
                            />
                          )
                        )}
                      </div>
                    )}
                  </div>
                )}

                {loadMoreError && (
                  <div className="mt-7 rounded-xl border border-gray-200 bg-gray-50 px-4 py-5 text-center">
                    <p className="text-sm text-gray-600">
                      More books could not be loaded.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setLoadMoreError('');
                        fetchBooks({
                          page:
                            pagination.nextPage || pagination.page + 1,
                          append: true,
                        });
                      }}
                      className="mt-3 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
                    >
                      Try again
                    </button>
                  </div>
                )}
              </>
            )}
            </div>
          </div>
        </section>

      </main>

      <footer className="border-t border-gray-100 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-7 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="text-center text-sm text-gray-500 sm:text-left">
              &copy; {new Date().getFullYear()} PawaLibrary. All rights reserved.
            </p>

            <nav
              aria-label="Footer navigation"
              className="flex flex-wrap items-center justify-center gap-x-5 gap-y-3 text-sm font-medium"
            >
              <Link to="/privacy-policy" className="text-gray-500 transition-colors hover:text-primary-600">
                Privacy Policy
              </Link>
              <Link to="/login" className="text-gray-500 transition-colors hover:text-primary-600">
                Sign in
              </Link>
              <Link to="/register" className="text-gray-500 transition-colors hover:text-primary-600">
                Create account
              </Link>
            </nav>
          </div>
        </div>
      </footer>

      {selectedBook && (
        <div
          className={`landing-modal-backdrop fixed inset-0 z-[70] flex items-end justify-center bg-black/30 p-0 backdrop-blur-[2px] sm:items-center sm:p-4 ${
            modalVisible ? 'visible' : ''
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="public-book-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeBookModal();
          }}
        >
          <div
            className={`landing-modal-panel w-full max-w-lg rounded-t-3xl border border-white bg-white p-5 shadow-[0_20px_60px_rgba(15,23,42,.16)] sm:rounded-3xl sm:p-6 ${
              modalVisible ? 'visible' : ''
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 gap-4">
                <div className="relative h-36 w-24 flex-none overflow-hidden rounded-xl border border-gray-100 bg-white sm:h-40 sm:w-28">
                  <BookCover book={selectedBook} eager />
                </div>
                <div className="min-w-0 pt-1">
                  <p className="text-xs font-semibold text-primary-600">
                    {selectedBook.sector || 'General'}
                  </p>
                  <h2 id="public-book-title" className="mt-1 line-clamp-2 text-lg font-bold">
                    {selectedBook.title}
                  </h2>
                  <p className="mt-1 truncate text-sm text-gray-500">
                    {selectedBook.author || 'Unknown author'}
                  </p>
                  <p className="mt-2 text-sm font-semibold text-gray-800">
                    {formatPrice(selectedBook)}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeBookModal}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {selectedBook.description && (
              <p className="mt-5 line-clamp-3 text-sm leading-6 text-gray-600">
                {selectedBook.description}
              </p>
            )}

            <div className="mt-5 rounded-2xl border border-primary-100 bg-primary-50/70 p-4">
              <div className="flex gap-3">
                <LockClosedIcon className="mt-0.5 h-5 w-5 flex-none text-primary-600" />
                <div>
                  <p className="font-semibold text-gray-900">Sign in to read this book</p>
                  <p className="mt-1 text-sm leading-5 text-gray-600">
                    Create an account or sign in to continue reading securely.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <Link
                to={loginRedirect}
                className="inline-flex items-center justify-center rounded-xl border border-gray-300 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
              >
                Sign in
              </Link>
              <Link
                to={registerRedirect}
                className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
              >
                Create account
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}